import { ref, watch, computed, unref, reactive, onUnmounted } from '@vue/composition-api';
import { useQuery } from 'vue-query';
import { createContext, useContext } from '@berlin-front/common/composables/context';
import { BerlinBffContext } from '@services/BerlinBff';
import type { ContextValueType } from '@berlin-front/common/composables/context';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import { ApiCacheContext } from '@/services/ApiCacheContext';
import { ROUTE_NAME } from '@/router/route-name';

const MAX_POLLING_MS = 1800000; // 30分
const POLLING_INTERVAL_MS = 1000; // 1秒

export const useAuthCall = () => {
  const router = useRouter();
  const { atoneModalParameter, atoneCapturePostResult, tokens, userFullName } = useContext(ApiCacheContext);

  const fullName = userFullName;

  const { payment } = useContext(BerlinBffContext);

  const npToken = computed(() => unref(tokens).npToken);
  const transactionId = atoneCapturePostResult.value?.id;

  const abortController = new AbortController();

  // Queryの定義 & 初回実行
  const { refetch, data: response } = useQuery(
    'atone.payment.authorizeCallStatusGet',
    () => {
      // ログアウトする -> npTokenが変更となり、watchでabortしてリクエストは停止される。
      if (abortController.signal.aborted || !npToken.value) {
        // これはすぐにonErrorで処理される。
        throw new Error('aborted');
      }
      return payment.atonePaymentControllerAuthorizeCallStatusGet(
        transactionId || '',
        unref(npToken) || '',
        atoneModalParameter.value.pubKey,
        {
          signal: abortController.signal,
        },
      );
    },
    {
      onError: () => {
        // 握りつぶす。
        // throw new Error('aborted') されたやつがここでキャプチャされる。
        // ここでハンドルするとApo.vueに例外が飛ばないのでシステムエラーにならない。
      },
    },
  );
  const tel = computed(() => response.value?.data.dial);
  const passcode = ref(''); // TODO: 後払いの時に実装

  // ポーリングの設定
  const startTime = new Date().getTime();
  const timeoutId = ref(NaN);
  timeoutId.value = window.setTimeout(function run() {
    // abort済みなら処理をストップする。
    if (abortController.signal.aborted) return;

    void (async () => {
      try {
        await refetch.value();
      } catch {
        /* */
      }
      if (new Date().getTime() - startTime < MAX_POLLING_MS) {
        timeoutId.value = window.setTimeout(run, POLLING_INTERVAL_MS);
      } else {
        // TODO:時間経過後の処理（後回し）
      }
    })();
  }, POLLING_INTERVAL_MS);

  // tokenの値が変わったらポーリングをやめる。
  watch(npToken, () => {
    abortController.abort();
    window.clearTimeout(timeoutId.value);
  });

  // 画面離脱時のリクエストはキャンセルするし、ポーリングも止める。
  onUnmounted(() => {
    abortController.abort();
    window.clearTimeout(timeoutId.value);
  });

  watch(response, () => {
    if (response.value?.data.checked === true) {
      // trueになったらポーリングを止めて画面遷移。
      abortController.abort();
      window.clearTimeout(timeoutId.value);
      void router.replace({ name: ROUTE_NAME.ATONE_LOADING_REAUTHORIZE });
    }
  });

  return reactive({
    fullName,
    response,
    tel,
    passcode,
  });
};

export const CallAuthContext = createContext({
  useAuthCall,
});
export type CallAuthContextType = ContextValueType<typeof CallAuthContext>;
